/**
 * The world this prototype runs on: a small catalogue, the contracts in that
 * own it, three licensees and the contracts out licensing tracks to them.
 *
 * Two things in here are deliberate and worth keeping if the data is edited:
 *  - "Neon Harbour" (the Nova Skye contract in) has tracks in TWO contracts out,
 *    which is the case the Contracts out tab on a contract in exists for.
 *  - Every contract out's tracks come from a contract in, so licensing revenue
 *    always has payees to land on.
 */
import type { FormatReleased, Licensee } from "@/types";

export const LABEL_ID = "dev-label";
export const LABEL_NAME = "Dev Label";

const now = "2026-01-15T10:00:00Z";

type SeedRelease = {
  id: string;
  title: string;
  type: "album" | "single" | "ep" | "compilation";
  release_date: string;
};

type SeedRecording = {
  id: string;
  title: string;
  mix_name: string | null;
  isrc: string;
  release: string;
  track_number: string;
  artist: string;
  /** A track can credit more than one artist; one seeded track does. */
  featuring?: string;
};

export const RELEASES: SeedRelease[] = [
  { id: "rel-neon-harbour", title: "Neon Harbour", type: "album", release_date: "2025-03-14" },
  { id: "rel-paper-lanterns", title: "Paper Lanterns", type: "ep", release_date: "2025-06-06" },
  { id: "rel-dust-and-gold", title: "Dust and Gold", type: "album", release_date: "2024-10-25" },
  { id: "rel-slow-tide", title: "Slow Tide", type: "single", release_date: "2025-09-12" },
];

export const RECORDINGS: SeedRecording[] = [
  // Nova Skye — Neon Harbour
  {
    id: "rec-midnight-signal",
    title: "Midnight Signal",
    mix_name: null,
    isrc: "GBdev2500001",
    release: "rel-neon-harbour",
    track_number: "1",
    artist: "Nova Skye",
  },
  {
    id: "rec-harbour-lights",
    title: "Harbour Lights",
    mix_name: null,
    isrc: "GBdev2500002",
    release: "rel-neon-harbour",
    track_number: "2",
    artist: "Nova Skye",
  },
  {
    id: "rec-glass-avenue",
    title: "Glass Avenue",
    mix_name: "Radio Edit",
    isrc: "GBdev2500003",
    release: "rel-neon-harbour",
    track_number: "3",
    artist: "Nova Skye",
    featuring: "Rae Dunbar",
  },
  // Marcus Vale — Paper Lanterns
  {
    id: "rec-paper-lanterns",
    title: "Paper Lanterns",
    mix_name: null,
    isrc: "GBdev2500004",
    release: "rel-paper-lanterns",
    track_number: "1",
    artist: "Marcus Vale",
  },
  {
    id: "rec-low-country",
    title: "Low Country",
    mix_name: null,
    isrc: "GBdev2500005",
    release: "rel-paper-lanterns",
    track_number: "2",
    artist: "Marcus Vale",
  },
  // The Hollow Pines — Dust and Gold
  {
    id: "rec-dust-and-gold",
    title: "Dust and Gold",
    mix_name: null,
    isrc: "GBdev2500006",
    release: "rel-dust-and-gold",
    track_number: "1",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-copper-wire",
    title: "Copper Wire",
    mix_name: "Live",
    isrc: "GBdev2500007",
    release: "rel-dust-and-gold",
    track_number: "2",
    artist: "The Hollow Pines",
  },
  {
    id: "rec-long-winter",
    title: "Long Winter",
    mix_name: null,
    isrc: "GBdev2500008",
    release: "rel-dust-and-gold",
    track_number: "3",
    artist: "The Hollow Pines",
  },
  // Unsigned — Slow Tide
  {
    id: "rec-slow-tide",
    title: "Slow Tide",
    mix_name: null,
    isrc: "GBdev2500009",
    release: "rel-slow-tide",
    track_number: "1",
    artist: "Nova Skye",
  },
];

export const PAYEES = [
  {
    id: "payee-nova-skye",
    name: "Nova Skye",
    legal_name: "Skyeline Music Ltd",
    contact_email: "hello@novaskye.example",
    country: "GB",
    city: "Bristol",
    preferred_currency: "USD",
  },
  {
    id: "payee-marcus-vale",
    name: "Marcus Vale",
    legal_name: "Vale Recordings LLC",
    contact_email: "marcus@valerecordings.example",
    country: "US",
    city: "Nashville",
    preferred_currency: "USD",
  },
  {
    id: "payee-hollow-pines",
    name: "The Hollow Pines",
    legal_name: "Hollow Pines Collective",
    contact_email: "band@hollowpines.example",
    country: "CA",
    city: "Toronto",
    preferred_currency: "USD",
  },
  {
    id: "payee-june-ashworth",
    name: "June Ashworth",
    legal_name: "J. Ashworth Productions",
    contact_email: "june@ashworth.example",
    country: "GB",
    city: "Manchester",
    preferred_currency: "GBP",
  },
].map((payee) => ({
  ...payee,
  is_active: true,
  label: LABEL_ID,
  label_name: LABEL_NAME,
  created_at: now,
  updated_at: now,
  can_delete: true,
}));

/** The licensees a contract out can be with — the list the Licensees page shows. */
export function seedLicensees(): Licensee[] {
  return [
    {
      id: "licensee-ministry",
      name: "Ministry of Compilations",
      legal_name: "Ministry of Compilations Ltd",
      contact_name: "Priya Raman",
      contact_email: "licensing@ministrycomp.example",
      phone: "",
      website: "ministrycomp.example",
      tax_number: "GB884120993",
      country: "GB",
      city: "London",
      state: "",
      zip_code: "EC1V 9NR",
      address: "44 Clerkenwell Road",
      address_line_2: "",
    },
    {
      id: "licensee-northbound",
      name: "Northbound Records",
      legal_name: "Northbound Records BV",
      contact_name: "Sanne de Vries",
      contact_email: "deals@northbound.example",
      phone: "",
      website: "northbound.example",
      tax_number: "NL812345678B01",
      country: "NL",
      city: "Amsterdam",
      state: "",
      zip_code: "1017 CG",
      address: "Keizersgracht 210",
      address_line_2: "",
    },
    {
      id: "licensee-cue-crescendo",
      name: "Cue & Crescendo Sync",
      legal_name: "Cue and Crescendo LLC",
      contact_name: "Dana Whitfield",
      contact_email: "clearances@cuecrescendo.example",
      phone: "",
      website: "cuecrescendo.example",
      tax_number: "US-88-1204471",
      country: "US",
      city: "Los Angeles",
      state: "CA",
      zip_code: "90028",
      address: "6350 Sunset Blvd",
      address_line_2: "Suite 410",
    },
  ].map((licensee) => ({
    ...licensee,
    is_active: true,
    label: LABEL_ID,
    label_name: LABEL_NAME,
    created_at: now,
    updated_at: now,
    can_delete: true,
  }));
}

/** A contract in, as this prototype's mock keeps it: the API shape plus its track ids. */
export type StoredContract = {
  id: string;
  name: string;
  contract_type: string;
  status: string;
  start_date: string;
  end_date: string | null;
  territory: string;
  currency: string;
  statement_period: string;
  label_share_percent: string;
  label_share_overrides: Record<string, string>;
  payment_threshold: string;
  advance_amount: string;
  payees: { payee: string; share_percentage: number }[];
  track_ids: string[];
  file: string | null;
};

export function seedContracts(): StoredContract[] {
  return [
    {
      id: "contract-nova-skye",
      name: "Nova Skye — Recording Agreement",
      contract_type: "recording",
      status: "active",
      start_date: "2024-11-01",
      end_date: "2027-10-31",
      territory: "worldwide",
      currency: "USD",
      statement_period: "half_yearly",
      label_share_percent: "50.00",
      // Licensing is the rate a contract out's revenue comes back at.
      label_share_overrides: { streaming: "55.00", licensing: "60.00" },
      payment_threshold: "50.00",
      advance_amount: "0.00",
      payees: [{ payee: "payee-nova-skye", share_percentage: 100 }],
      track_ids: ["rec-midnight-signal", "rec-harbour-lights", "rec-glass-avenue", "rec-slow-tide"],
      file: null,
    },
    {
      id: "contract-marcus-vale",
      name: "Marcus Vale — Recording Agreement",
      contract_type: "recording",
      status: "active",
      start_date: "2025-01-01",
      end_date: "2028-12-31",
      territory: "worldwide",
      currency: "USD",
      statement_period: "quarterly",
      label_share_percent: "45.00",
      label_share_overrides: { licensing: "50.00" },
      payment_threshold: "50.00",
      advance_amount: "0.00",
      payees: [
        { payee: "payee-marcus-vale", share_percentage: 80 },
        { payee: "payee-june-ashworth", share_percentage: 20 },
      ],
      track_ids: ["rec-paper-lanterns", "rec-low-country"],
      file: null,
    },
    {
      id: "contract-hollow-pines",
      name: "The Hollow Pines — Distribution Deal",
      contract_type: "distribution",
      status: "active",
      start_date: "2024-05-01",
      end_date: null,
      territory: "north_america",
      currency: "USD",
      statement_period: "monthly",
      label_share_percent: "30.00",
      label_share_overrides: {},
      payment_threshold: "25.00",
      advance_amount: "0.00",
      payees: [{ payee: "payee-hollow-pines", share_percentage: 100 }],
      track_ids: ["rec-dust-and-gold", "rec-copper-wire", "rec-long-winter"],
      file: null,
    },
  ];
}

/** A contract out, as the mock keeps it. */
export type StoredContractOut = {
  id: string;
  name: string;
  status: string;
  licensing_type: string;
  start_date: string | null;
  end_date: string | null;
  release_date: string | null;
  formats_released: FormatReleased[];
  territory: string;
  currency: string;
  licensee_royalty_percent: string;
  statement_period: string;
  advance_per_track: string;
  recoupable: string;
  recoupable_amount: string | null;
  additional_notes: string | null;
  licensee: string | null;
  /** The licensee's name, copied on so a row can print it without a second call. */
  licensee_name: string | null;
  track_ids: string[];
  file: string | null;
  file_name: string | null;
};

export function seedContractsOut(): StoredContractOut[] {
  return [
    {
      id: "contract-out-summer-anthems",
      name: "Summer Anthems 2026",
      status: "active",
      licensing_type: "compilation",
      start_date: "2026-02-01",
      end_date: "2028-01-31",
      release_date: "2026-06-19",
      formats_released: ["cd", "digital", "streaming"],
      territory: "worldwide",
      currency: "USD",
      licensee_royalty_percent: "12.00",
      statement_period: "half_yearly",
      advance_per_track: "250.00",
      recoupable: "partially",
      // For the whole contract: 750 of advance across three tracks, 200 of it recoups.
      recoupable_amount: "200.00",
      additional_notes:
        "Pro-rata share of the compilation; the licensee calculates it and sends the statement. Mechanicals paid at source.",
      licensee: "licensee-ministry",
      licensee_name: "Ministry of Compilations",
      // One track each from Nova Skye, Marcus Vale and The Hollow Pines: a
      // contract out is not tied to a single contract in.
      track_ids: ["rec-midnight-signal", "rec-paper-lanterns", "rec-dust-and-gold"],
      file: null,
      file_name: null,
    },
    {
      id: "contract-out-northbound-eu",
      name: "Northbound EU Sub-license",
      status: "active",
      licensing_type: "sub_license",
      start_date: "2025-09-01",
      end_date: "2027-08-31",
      release_date: "2025-10-03",
      formats_released: ["vinyl", "cd", "digital"],
      territory: "europe",
      currency: "EUR",
      licensee_royalty_percent: "18.00",
      statement_period: "quarterly",
      advance_per_track: "400.00",
      recoupable: "fully",
      recoupable_amount: null,
      additional_notes: null,
      licensee: "licensee-northbound",
      licensee_name: "Northbound Records",
      // Nova Skye again — the same contract in, licensed out twice.
      track_ids: ["rec-midnight-signal", "rec-harbour-lights"],
      file: null,
      file_name: null,
    },
    {
      id: "contract-out-late-night-drives",
      name: "Late Night Drives Vol. 3",
      status: "draft",
      licensing_type: "compilation",
      start_date: "2026-04-01",
      end_date: "2029-03-31",
      release_date: null,
      formats_released: [],
      territory: "worldwide_except_usa",
      currency: "USD",
      licensee_royalty_percent: "10.00",
      statement_period: "annually",
      advance_per_track: "150.00",
      recoupable: "fully",
      recoupable_amount: null,
      additional_notes: null,
      // Still being set up: the tracks are picked, the licensee is not. Keeps the
      // contract progress banner and the disabled Activate button reachable.
      licensee: null,
      licensee_name: null,
      track_ids: ["rec-dust-and-gold", "rec-copper-wire"],
      file: null,
      file_name: null,
    },
    {
      // The same licensee, a second deal, and one track it already had: the case
      // where a licensee's Licensed tracks tab shows a track twice, once per
      // contract out.
      id: "contract-out-summer-anthems-deluxe",
      name: "Summer Anthems 2026 — Deluxe",
      status: "active",
      licensing_type: "compilation",
      start_date: "2026-06-01",
      end_date: "2028-05-31",
      release_date: "2026-07-10",
      formats_released: ["digital", "streaming"],
      territory: "worldwide",
      currency: "USD",
      licensee_royalty_percent: "14.00",
      statement_period: "half_yearly",
      advance_per_track: "300.00",
      recoupable: "not",
      recoupable_amount: null,
      additional_notes:
        "Deluxe edition of the same compilation — the advance here is a flat fee the label keeps.",
      licensee: "licensee-ministry",
      licensee_name: "Ministry of Compilations",
      track_ids: ["rec-midnight-signal", "rec-glass-avenue"],
      file: null,
      file_name: null,
    },
    {
      id: "contract-out-coastline-sync",
      name: "Coastline Sync Package",
      status: "ended",
      licensing_type: "sub_license",
      start_date: "2024-06-01",
      end_date: "2025-05-31",
      release_date: "2024-08-15",
      // Sync: none of the release formats applies.
      formats_released: [],
      territory: "north_america",
      currency: "USD",
      licensee_royalty_percent: "25.00",
      statement_period: "half_yearly",
      advance_per_track: "1200.00",
      recoupable: "fully",
      recoupable_amount: null,
      additional_notes: null,
      licensee: "licensee-cue-crescendo",
      licensee_name: "Cue & Crescendo Sync",
      track_ids: ["rec-long-winter", "rec-low-country"],
      file: null,
      file_name: null,
    },
  ];
}

/** The vendors the revenue upload offers, licensing among them. */
/**
 * Importing a statement from a licensee is not designed yet — licensing revenue
 * goes in as a sales entry for now. The vendor and the upload's Contract out
 * field stay in the code; flip this to bring them back.
 */
export const SHOW_LICENSEE_STATEMENTS = false;

/**
 * The statement formats the upload offers, named as in the app's last
 * hard-coded vendor list (dash, before 138742d moved it to /imports/vendors).
 * A vendor is whoever sends the statement file — a distributor — not the DSPs
 * (Spotify, Apple Music) that show up inside it.
 */
export const VENDORS = [
  {
    id: "above_board",
    name: "Above Board",
    default_currency: null,
    supported_import_types: ["statement"],
  },
  { id: "k7", name: "K7", default_currency: null, supported_import_types: ["statement"] },
  {
    id: "label_worx",
    name: "Label Worx",
    default_currency: null,
    supported_import_types: ["statement"],
  },
  {
    id: "the_orchard_legacy",
    name: "The Orchard (Legacy Format)",
    default_currency: null,
    supported_import_types: ["statement"],
  },
  {
    id: "the_orchard_new_2025",
    name: "The Orchard (New Version 2025)",
    default_currency: null,
    supported_import_types: ["statement"],
  },
  {
    id: "vendor-third-party-licensing",
    name: "Third party licensing",
    default_currency: null,
    supported_import_types: ["statement"],
    // What makes the upload form ask which contract out the statement is for.
    requires_contract_out: true,
  },
  {
    id: "vendor-catalog-csv",
    name: "Catalog CSV",
    default_currency: null,
    supported_import_types: ["catalog"],
  },
];

// --- Revenue ---------------------------------------------------------------

/**
 * A few statements, so the Revenue page has the list the simple sales sit next
 * to — both its versions need one. Only what the list shows is filled in.
 */
export function seedRevenueStatements() {
  const statement = (
    id: string,
    vendor: string,
    period_start: string,
    period_end: string,
    revenue: string,
    settled: boolean,
    date_received: string | null,
  ) => ({
    id,
    file_name: `${id}.csv`,
    import_batch: `batch-${id}`,
    label: LABEL_ID,
    uploaded_by: "dev-user",
    vendor,
    currency: "USD",
    period_start,
    period_end,
    statement_received_date: period_end,
    file: null,
    status: "processed",
    total_lines: 120,
    revenue,
    created_at: now,
    updated_at: now,
    name: null,
    can_delete: true,
    amount_received: settled ? revenue : null,
    exchange_rate: null,
    spot_rate: null,
    spot_rate_date: null,
    date_received,
    is_settled: settled,
    is_settling: false,
    settlement_error: "",
    settlement_difference_reason: null,
    settlement_quantity_difference: settled ? "0.00" : null,
    settlement_fx_difference: settled ? "0.00" : null,
    expected_amount: settled ? null : revenue,
    expected_foreign_amount: null,
  });
  return [
    statement(
      "stmt-orchard-q2",
      "the_orchard_new_2025",
      "2026-04-01",
      "2026-06-30",
      "4820.35",
      true,
      "2026-08-14",
    ),
    statement(
      "stmt-label-worx-q2",
      "label_worx",
      "2026-04-01",
      "2026-06-30",
      "1936.10",
      false,
      null,
    ),
    statement("stmt-k7-h1", "k7", "2026-01-01", "2026-06-30", "612.40", false, null),
  ];
}

export type StoredSimpleSalesEntry = {
  id: string;
  revenue_type: "licensing" | "physical" | "digital";
  contract_out: string | null;
  sales_date: string;
  currency: string;
  /** Physical products: the release, its amount and units sold. */
  release_id?: string | null;
  amount?: string | null;
  units?: number | null;
  /** Licensing and digital sales: each track with its own amount (units for digital). */
  lines: { track_id: string; amount: string; units?: number | null }[];
  country: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string;
};

/** One of each kind of revenue a simple sales entry is for. */
export function seedSimpleSales(): StoredSimpleSalesEntry[] {
  return [
    {
      // A sync flat fee: one track, one fee, no statement.
      id: "sale-coastline-sync-fee",
      revenue_type: "licensing",
      contract_out: "contract-out-coastline-sync",
      sales_date: "2024-09-02",
      currency: "USD",
      lines: [{ track_id: "rec-long-winter", amount: "1500.00" }],
      country: "US",
      reference: "CC-SYNC-0419",
      notes: "Flat fee for use in the Coastline trailer.",
      created_at: now,
    },
    {
      // The advance from a licensee — paid upfront, so it never shows up on a
      // licensee statement. 250 per track across the contract out's three tracks.
      id: "sale-summer-anthems-advance",
      revenue_type: "licensing",
      contract_out: "contract-out-summer-anthems",
      sales_date: "2026-02-20",
      currency: "USD",
      lines: [
        { track_id: "rec-midnight-signal", amount: "250.00" },
        { track_id: "rec-paper-lanterns", amount: "250.00" },
        { track_id: "rec-dust-and-gold", amount: "250.00" },
      ],
      country: null,
      reference: "MOC-ADV-2026-03",
      notes: null,
      created_at: now,
    },
    {
      // Paid in euros, so the list shows both the amount received and what it
      // comes to in the label currency.
      id: "sale-northbound-advance",
      revenue_type: "licensing",
      contract_out: "contract-out-northbound-eu",
      sales_date: "2025-10-06",
      currency: "EUR",
      lines: [
        { track_id: "rec-midnight-signal", amount: "400.00" },
        { track_id: "rec-harbour-lights", amount: "400.00" },
      ],
      country: null,
      reference: null,
      notes: null,
      created_at: now,
    },
    {
      // Merch table sales: CDs and vinyl of one release.
      id: "sale-dust-and-gold-tour",
      revenue_type: "physical",
      contract_out: null,
      sales_date: "2026-05-17",
      currency: "USD",
      // The whole release, one amount, and how many were sold.
      release_id: "rel-dust-and-gold",
      amount: "450.00",
      units: 36,
      lines: [],
      country: "GB",
      reference: null,
      notes: "CDs and vinyl sold at the merch table on the spring tour.",
      created_at: now,
    },
    {
      // Downloads sold from the label's own site.
      id: "sale-webstore-june",
      revenue_type: "digital",
      contract_out: null,
      sales_date: "2026-06-30",
      currency: "USD",
      lines: [
        { track_id: "rec-paper-lanterns", amount: "52.00", units: 40 },
        { track_id: "rec-low-country", amount: "32.00", units: 25 },
      ],
      country: null,
      reference: "WEB-2026-06",
      notes: null,
      created_at: now,
    },
  ];
}
